import {Component, EventEmitter, HostListener, Output} from '@angular/core';
import {CriteriaModel} from 'app/_models/criteria.model';
import {ProjectPlanningService} from 'app/_services';
import {MessageService, ConfirmationService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import { CriteriaTranslateModel } from './models/criteria-translate.model';
import { AITranslationService } from 'app/_services/ai-translation.service';

@Component({
  moduleId: module.id,
  selector: 'app-criteria',
  templateUrl: './criteria.component.html',
  styleUrls: ['./criteria-style.scss']
})
export class CriteriaComponent {
  public currentLanguage = localStorage.getItem('Language');
  public activeLangTabIndex: number = 0;
  public showModal = false;
  criteria = new CriteriaModel();
  roleIndex = null;
  public fieldTranslations = new CriteriaTranslateModel();
  public canEdit = true;
  public isTranslating = false;
  public updatingCriteria = false;
  public criteriaNameModal = false;

  // Local display variables for time pickers (not saved to DB)
  public beforeHoursDisplay: Date = null;
  public afterHoursDisplay: Date = null;

  // Local variables for hour type selection
  public hourTypeBefore = false;
  public hourTypeAfter = false;

  // ---- Weekly time-criteria matrix (JS-1618) ----------------------------------
  // One column per weekday (Mo–Su), one row per hour. A criterion is simply the
  // set of shaded cells, so an overnight / multi-day span reads as one continuous
  // run and is never split into separate boxes (Fabio's requirement). On save the
  // grid is decomposed into `time_windows` rows the existing wage/expense calc
  // already understands; on load those rows are expanded back onto the grid.
  public readonly days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  public readonly hours = Array.from({ length: 24 }, (_, h) => h);
  // grid[hour][dayIndex] — dayIndex 0 = Monday … 6 = Sunday (matches the availability grid).
  public grid: boolean[][] = [];
  private painting = false;
  private paintValue = true;
  // Preset tick state is tracked separately from the grid so "de/select all" (and
  // manual painting) never causes the preset chips to appear ticked. When one or
  // more presets are ticked, the grid is painted as the intersection of them.
  public activePresets: { evenings: boolean; weekends: boolean; nights: boolean } = {
    evenings: false, weekends: false, nights: false,
  };

  public languages = [
    { value: 'en', label: 'English', labelKey: 'English' },
    { value: 'de', label: 'Deutsch', labelKey: 'German' },
    { value: 'fr', label: 'Français', labelKey: 'French' },
    { value: 'it', label: 'Italiano', labelKey: 'Italian' }
  ];

  @Output() saved = new EventEmitter<any>();

  constructor(private projectPlanningService: ProjectPlanningService,
              private messageService: MessageService,
              private confirmationService: ConfirmationService,
              private translate: TranslateService,
              private aiTranslationService: AITranslationService) {
    this.resetGrid();
  }

  ngOnInit() {
    this.activeLangTabIndex = this.selectedTab() || 0;
  }

  show(type, name, index) {
    this.criteria.type = type;
    this.criteria.name = name;
    this.fieldTranslations = new CriteriaTranslateModel();
    this.fieldTranslations.name[this.currentLanguage] = name;
    this.roleIndex = index;
    this.canEdit = true;
    this.showModal = true;
    // Reset local display variables
    this.beforeHoursDisplay = null;
    this.afterHoursDisplay = null;
    this.hourTypeBefore = false;
    this.hourTypeAfter = false;
    this.resetGrid();
  }

  editCriteria(criteriaId, canEdit = true) {
    this.showModal = true;
    this.canEdit = canEdit;
    this.projectPlanningService.getCriteria(criteriaId).subscribe( criteria => {
      this.criteria = criteria;
      this.fieldTranslations = JSON.parse(criteria.field_translations);
      // Paint the weekly matrix from the stored time windows (with a legacy
      // single-window fallback for criteria saved before the windows table).
      this.buildGridFromWindows(
        (criteria.time_windows && criteria.time_windows.length)
          ? criteria.time_windows
          : (criteria.time && criteria.from_time && criteria.to_time)
            ? [this.legacyWindowFromCriteria(criteria)]
            : []
      );
      // Convert decimal hours to display format (Date object for p-calendar)
      this.beforeHoursDisplay = this.decimalToHoursDisplay(criteria.before_hours);
      this.afterHoursDisplay = this.decimalToHoursDisplay(criteria.after_hours);
      // Set hour type based on which value is set
      this.hourTypeBefore = !!criteria.before_hours;
      this.hourTypeAfter = !!criteria.after_hours;
    })
  }

  // Convert decimal hours (e.g., 1.5) to Date object for time picker display
  decimalToHoursDisplay(decimalHours: number): Date | null {
    if (!decimalHours && decimalHours !== 0) return null;
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  // Convert Date object from time picker to decimal hours for backend
  hoursDisplayToDecimal(date: Date): number | null {
    if (!date) return null;
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return hours + (minutes / 60);
  }

  close() {
    this.criteria = new CriteriaModel();
    this.roleIndex = null;
    this.showModal = false;
    // Reset local display variables
    this.beforeHoursDisplay = null;
    this.afterHoursDisplay = null;
    this.hourTypeBefore = false;
    this.hourTypeAfter = false;
    this.resetGrid();
  }

  criteriaTypeChanged(type) {
    if (this.criteria[type]) {
      this.criteria.time = false;
      this.criteria.hour = false;
      this.criteria.no_criteria = false;
      this.criteria[type] = true;
    } else {
      this.criteria.no_criteria = true;
    }

    if(type === 'time') {
      // The matrix carries the selection now; the legacy scalar columns are
      // filled server-side from the first derived window.
      this.criteria.from_time = null;
      this.criteria.to_time = null;
      // Clear hour criteria
      this.criteria.before_hours = null;
      this.criteria.after_hours = null;
      this.beforeHoursDisplay = null;
      this.afterHoursDisplay = null;
      this.hourTypeBefore = false;
      this.hourTypeAfter = false;
      // Start from an empty grid — the user shades the hours that apply.
      this.resetGrid();
    } else if(type === 'hour') {
      this.criteria.from_time = null;
      this.criteria.to_time = null;
      // Reset hour type selection and auto-select "before" by default
      this.criteria.before_hours = null;
      this.criteria.after_hours = null;
      this.beforeHoursDisplay = null;
      this.afterHoursDisplay = null;
      this.hourTypeBefore = true;  // Auto-select before by default
      this.hourTypeAfter = false;
      this.criteria.time_windows = [];
    } else {
      this.criteria.from_time = null;
      this.criteria.to_time = null;
      this.criteria.before_hours = null;
      this.criteria.after_hours = null;
      this.beforeHoursDisplay = null;
      this.afterHoursDisplay = null;
      this.hourTypeBefore = false;
      this.hourTypeAfter = false;
      this.criteria.time_windows = [];
    }
  }

  hourTypeChanged(type) {
    if (type === 'before') {
      if (this.hourTypeBefore) {
        // Selecting before - deselect after and clear its value
        this.hourTypeAfter = false;
        this.afterHoursDisplay = null;
        this.criteria.after_hours = null;
      } else {
        // Trying to deselect before - must have at least one selected
        // If after is not selected, force before to stay selected
        if (!this.hourTypeAfter) {
          // Use setTimeout to ensure Angular picks up the change
          setTimeout(() => this.hourTypeBefore = true, 0);
        }
      }
    } else if (type === 'after') {
      if (this.hourTypeAfter) {
        // Selecting after - deselect before and clear its value
        this.hourTypeBefore = false;
        this.beforeHoursDisplay = null;
        this.criteria.before_hours = null;
      } else {
        // Trying to deselect after - must have at least one selected
        // If before is not selected, force after to stay selected
        if (!this.hourTypeBefore) {
          // Use setTimeout to ensure Angular picks up the change
          setTimeout(() => this.hourTypeAfter = true, 0);
        }
      }
    }
  }

  // Check if switching to a type is allowed (only blocked if the OTHER type has a value)
  canSwitchToType(type: string): boolean {
    if (type === 'before') {
      return !this.criteria.after_hours;
    } else {
      return !this.criteria.before_hours;
    }
  }

  // ---- Matrix state & interaction --------------------------------------------

  resetGrid() {
    this.grid = this.hours.map(() => this.days.map(() => false));
    this.painting = false;
    this.activePresets = { evenings: false, weekends: false, nights: false };
  }

  pad(n: number): string {
    return (n < 10 ? '0' : '') + n;
  }

  hourLabel(h: number): string {
    return this.pad(h) + ':00 – ' + this.pad((h + 1) % 24) + ':00';
  }

  get totalHours(): number {
    let count = 0;
    for (let h = 0; h < 24; h++) { for (let d = 0; d < 7; d++) { if (this.grid[h][d]) { count++; } } }
    return count;
  }

  // Painting: press on a cell sets the paint value (invert of that cell), then
  // dragging across cells applies it. Mirrors the availability grid UX.
  onCellDown(hour: number, day: number, event: MouseEvent) {
    if (!this.canEdit || event.button === 2) { return; }
    event.preventDefault();
    this.painting = true;
    this.paintValue = !this.grid[hour][day];
    this.grid[hour][day] = this.paintValue;
  }

  onCellEnter(hour: number, day: number) {
    if (!this.canEdit || !this.painting) { return; }
    this.grid[hour][day] = this.paintValue;
  }

  @HostListener('document:pointerup')
  stopPainting() {
    this.painting = false;
  }

  // Click a day header: fill the whole column, or clear it if already full.
  toggleDay(day: number) {
    if (!this.canEdit) { return; }
    const anyOff = this.hours.some(h => !this.grid[h][day]);
    this.hours.forEach(h => this.grid[h][day] = anyOff);
  }

  // Click an hour label: fill that row across all days, or clear it if already full.
  toggleHour(hour: number) {
    if (!this.canEdit) { return; }
    const anyOff = this.days.some((_, d) => !this.grid[hour][d]);
    this.days.forEach((_, d) => this.grid[hour][d] = anyOff);
  }

  toggleAll() {
    if (!this.canEdit) { return; }
    const anyOff = this.hours.some(h => this.days.some((_, d) => !this.grid[h][d]));
    this.hours.forEach(h => this.days.forEach((_, d) => this.grid[h][d] = anyOff));
    // De/select all is a grid-level action; the preset chips must not follow it.
    this.activePresets = { evenings: false, weekends: false, nights: false };
  }

  // ---- Presets (toggle on/off), matching the availability grid vocabulary -----

  private cellsFor(preset: string): Array<[number, number]> {
    const cells: Array<[number, number]> = [];
    if (preset === 'evenings') {
      for (let h = 18; h < 24; h++) { for (let d = 0; d < 7; d++) { cells.push([h, d]); } }
    } else if (preset === 'weekends') {
      for (let h = 0; h < 24; h++) { cells.push([h, 5]); cells.push([h, 6]); }
    } else if (preset === 'nights') {
      for (let d = 0; d < 7; d++) {
        for (let h = 20; h < 24; h++) { cells.push([h, d]); }
        for (let h = 0; h < 6; h++) { cells.push([h, d]); }
      }
    }
    return cells;
  }

  isPresetOn(preset: string): boolean {
    return !!this.activePresets[preset];
  }

  togglePreset(preset: string) {
    if (!this.canEdit) { return; }
    if (!(preset in this.activePresets)) { return; }
    this.activePresets[preset] = !this.activePresets[preset];
    this.applyActivePresetsToGrid();
  }

  // When any presets are active, the grid is painted as the intersection of their
  // cell sets — e.g. evenings ∩ weekends = evening hours on Sat/Sun only. When
  // none are active, the grid is cleared. Manual painting still works on top,
  // but the chip tick state stays independent (see activePresets docs above).
  private applyActivePresetsToGrid() {
    const active = Object.keys(this.activePresets).filter(k => this.activePresets[k]);
    this.grid = this.hours.map(() => this.days.map(() => false));
    if (active.length === 0) { return; }
    const sets = active.map(p => new Set(this.cellsFor(p).map(([h, d]) => h * 7 + d)));
    for (let h = 0; h < 24; h++) {
      for (let d = 0; d < 7; d++) {
        if (sets.every(s => s.has(h * 7 + d))) { this.grid[h][d] = true; }
      }
    }
  }

  // ---- Grid <-> time_windows conversion --------------------------------------

  private parseHour(time: any): number {
    if (time instanceof Date) { return time.getHours(); }
    // Accept "HH:MM" or "HH:MM:SS"
    return parseInt(String(time).split(':')[0], 10) || 0;
  }

  private legacyWindowFromCriteria(criteria: any) {
    return {
      from_time: criteria.from_time,
      to_time: criteria.to_time,
      monday: criteria.monday, tuesday: criteria.tuesday, wednesday: criteria.wednesday,
      thursday: criteria.thursday, friday: criteria.friday, saturday: criteria.saturday, sunday: criteria.sunday,
    };
  }

  // Expand stored windows onto the grid. A window covers [from, to) on each of
  // its enabled days; when to <= from (or to = 00:00) it wraps past midnight and
  // the tail is painted onto the following day.
  buildGridFromWindows(windows: any[]) {
    this.resetGrid();
    (windows || []).forEach(w => {
      const from = this.parseHour(w.from_time);
      const to = this.parseHour(w.to_time);
      const enabled = this.days.map(d => !!w[d]);
      enabled.forEach((on, d) => {
        if (!on) { return; }
        if (to > from) {
          for (let h = from; h < to; h++) { this.grid[h][d] = true; }
        } else {
          // Wraps midnight: rest of this day, then the early hours of the next.
          for (let h = from; h < 24; h++) { this.grid[h][d] = true; }
          const next = (d + 1) % 7;
          for (let h = 0; h < to; h++) { this.grid[h][next] = true; }
        }
      });
    });
  }

  // Collapse the grid into time_windows. Each day's contiguous run of hours
  // becomes a segment; a run that reaches midnight is stored with to = 00:00
  // (an overnight window the calc credits to its start day), and a full 24h day
  // is split into two halves so we never emit a zero-length from == to window.
  // Identical segments are grouped so their days share a single window row.
  windowsFromGrid(): any[] {
    const groups: { [key: string]: any } = {};
    const addSegment = (fromH: number, toH: number, day: number) => {
      const from = this.pad(fromH) + ':00';
      const to = this.pad(toH % 24) + ':00';
      const key = from + '_' + to;
      if (!groups[key]) {
        groups[key] = {
          from_time: from, to_time: to,
          monday: false, tuesday: false, wednesday: false, thursday: false,
          friday: false, saturday: false, sunday: false,
        };
      }
      groups[key][this.days[day]] = true;
    };

    for (let d = 0; d < 7; d++) {
      let start = -1;
      for (let h = 0; h < 24; h++) {
        const on = this.grid[h][d];
        if (on && start < 0) { start = h; }
        const runEnds = on && (h === 23 || !this.grid[h + 1][d]);
        if (runEnds) {
          const end = h + 1; // exclusive, 1..24
          if (start === 0 && end === 24) {
            addSegment(0, 12, d);   // 00:00 -> 12:00
            addSegment(12, 24, d);  // 12:00 -> 00:00 (overnight to midnight)
          } else {
            addSegment(start, end, d);
          }
          start = -1;
        }
      }
    }
    return Object.keys(groups).map(k => groups[k]);
  }

  private applyGridToCriteria() {
    this.criteria.time_windows = this.criteria.time ? this.windowsFromGrid() : [];
  }

  // Short weekday label localized by the active translation tab (matches the legacy day labels).
  dayShortLabel(day: string, lang: string): string {
    const labels = {
      monday:    { de: 'Mo', fr: 'lun', def: 'Mo' },
      tuesday:   { de: 'Di', fr: 'mar', def: 'Tu' },
      wednesday: { de: 'Mi', fr: 'mer', def: 'We' },
      thursday:  { de: 'Do', fr: 'jeu', def: 'Th' },
      friday:    { de: 'Fr', fr: 'ven', def: 'Fr' },
      saturday:  { de: 'Sa', fr: 'sam', def: 'Sa' },
      sunday:    { de: 'So', fr: 'dim', def: 'Su' },
    };
    const l = labels[day];
    return lang === 'de' ? l.de : lang === 'fr' ? l.fr : l.def;
  }

  onSubmit() {
    this.criteria.field_translations = JSON.stringify(this.fieldTranslations);
    // Convert hour display values to decimal for backend
    if (this.criteria.hour) {
      this.criteria.before_hours = this.hoursDisplayToDecimal(this.beforeHoursDisplay);
      this.criteria.after_hours = this.hoursDisplayToDecimal(this.afterHoursDisplay);
    }
    // Validate before mutating the model, so a failed save never leaves half-converted state.
    if (!this.isCriteriaValid()) {
      return;
    }
    this.criteria.id ? this.updatingCriteria = true : this.storeCriteria();
  }

  selectedTab() {
    const languageIndex = { en: 0, de: 1, fr: 2, it: 3}
    return languageIndex[this.currentLanguage];
  }

  storeCriteria() {
    this.translate.get('Before proceeding, please review the settings for this criteria. Once saved, it will be editable only within agency settings. Are you sure you want to proceed?').subscribe(res => {
      this.confirmationService.confirm({
        key: 'criteriaConfirm',
        header: this.translate.instant("Confirmation"),
        message: res,
        accept: () => {
          this.criteria.field_translations = JSON.stringify(this.fieldTranslations);
          this.applyGridToCriteria();
          this.projectPlanningService.storeCriteria(this.criteria).subscribe(response => {
            this.saved.emit({ criteria_id: response.criteria_id, index: this.roleIndex});
            this.messageService.add({severity: 'success', summary: this.translate.instant('Success!'), detail: this.translate.instant('Criteria saved successfully!')});
            this.close();
          });
        }
      });
    });
  }

  updateCriteria() {
    this.criteria.field_translations = JSON.stringify(this.fieldTranslations);
    // Ensure hour values are converted to decimal for backend
    if (this.criteria.hour) {
      this.criteria.before_hours = this.hoursDisplayToDecimal(this.beforeHoursDisplay);
      this.criteria.after_hours = this.hoursDisplayToDecimal(this.afterHoursDisplay);
    }
    this.applyGridToCriteria();
    this.projectPlanningService.updateCriteria(this.criteria.id, this.criteria).subscribe( response => {
      this.saved.emit({ criteria_id: response.criteria_id, index: this.roleIndex});
      this.messageService.add({severity: 'success', summary: this.translate.instant('Success!'), detail: this.translate.instant('Criteria saved successfully!')});
      this.close();
    });
  }

  createNewCriteria(templateName) {
    this.criteria.id = null;
    this.criteria.name = templateName;
    this.criteria.field_translations = JSON.stringify(this.fieldTranslations);
    // Ensure hour values are converted to decimal for backend
    if (this.criteria.hour) {
      this.criteria.before_hours = this.hoursDisplayToDecimal(this.beforeHoursDisplay);
      this.criteria.after_hours = this.hoursDisplayToDecimal(this.afterHoursDisplay);
    }
    this.applyGridToCriteria();
    this.projectPlanningService.storeCriteria(this.criteria).subscribe(
      response => {
        if(response.message == "error") {
          this.messageService.add({severity: 'error', summary: this.translate.instant('Error!'), detail: this.translate.instant('This criteria template name already exists, please change the template name!')});
          return;
        }
        this.saved.emit({ criteria_id: response.criteria_id, index: this.roleIndex});
        this.messageService.add({severity: 'success', summary: this.translate.instant('Success!'), detail: this.translate.instant('Criteria saved successfully!')});
        this.criteriaNameModal = false;
        this.close();
      },
      error => {
        this.messageService.add({severity: 'error', summary: this.translate.instant('Error!')});
      }
    );
  }

  hasOtherLangContent(targetLang: string): boolean {
    const langs = ['en', 'de', 'fr', 'it'];
    return langs.some(l => l !== targetLang && !!(this.fieldTranslations.name[l] && this.fieldTranslations.name[l].trim()));
  }

  translateToLang(targetLang: string) {
    const langs = ['en', 'de', 'fr', 'it'];
    let sourceText = '';
    let sourceLang = '';
    for (const lang of langs) {
      if (lang !== targetLang && this.fieldTranslations.name[lang]?.trim()) {
        sourceText = this.fieldTranslations.name[lang];
        sourceLang = lang;
        break;
      }
    }

    this.isTranslating = true;
    this.aiTranslationService.translate(sourceText, sourceLang, targetLang).subscribe(
      response => {
        this.fieldTranslations.name[targetLang] = response.translated_text;
        this.isTranslating = false;
      },
      () => { this.isTranslating = false; }
    );
  }

  isCriteriaValid() {

    if(!this.criteria.name) {
      this.criteria.name = this.fieldTranslations.name[this.currentLanguage]
    }
    if (!this.criteria.name || this.criteria.name.trim().length === 0) {
        this.translate.get('Name is required').subscribe(res => {
            this.messageService.add({severity: 'error', summary: this.translate.instant('Error!'), detail: res});
        });
      return false;
    }
    if (this.criteria.hour) {
      if (!this.beforeHoursDisplay && !this.afterHoursDisplay) {
          this.translate.get('Up to or after hours is required').subscribe(res => {
              this.messageService.add({severity: 'error', summary: this.translate.instant('Error!'), detail: res});
          });
        return false;
      }
    }
    if (this.criteria.time) {
      if (this.totalHours === 0) {
        this.translate.get('Please select at least one hour in the time grid').subscribe(res => {
          this.messageService.add({severity: 'error', summary: this.translate.instant('Error!'), detail: res});
        });
        return false;
      }
    }
    return true;
  }

}
